/**
 * CMS FAB Contacts Query
 *
 * Server-side query for fetching FAB (Floating Action Button) contacts.
 */

import { query } from "@solidjs/router";
import type { CMSFabContactResponse, CMSFabContact } from "~/types/cms";
import { env } from "~/config/env";
import { buildFabContactsListUrl } from "../queryUrls";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS } from "../queryUtils";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";

/** 60s cache — FAB contacts rarely change */
const CACHE_TTL_MS = 60_000;
const MAX_CACHE_SIZE = 20;
const fabContactsCache = new Map<string, { data: CMSFabContact[]; expiresAt: number }>();

/** Clear the server-side FAB contacts Map cache. Called by webhook invalidation. */
export function clearFabContactsCache(tenantSlug?: string) {
  if (tenantSlug) {
    fabContactsCache.delete(tenantSlug);
  } else {
    fabContactsCache.clear();
  }
}

/**
 * Server-side query for fetching FAB contacts by tenant
 *
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @returns Array of active FAB contacts sorted by title, or empty array if none found
 */
export const getFabContactsByTenantQuery = query(
  async (tenantSlug: string): Promise<CMSFabContact[]> => {
    "use server";

    const cached = fabContactsCache.get(tenantSlug);
    if (cached && Date.now() < cached.expiresAt) {
      touchEntry(fabContactsCache, tenantSlug);
      return cached.data;
    }

    const LOG_PREFIX = "[fabContactsByTenant]";
    const url = `${env.cmsApiUrl}${buildFabContactsListUrl()}`;

    const data = await fetchFromCMS<CMSFabContactResponse>(url, { logPrefix: LOG_PREFIX });

    if (!data?.docs || !Array.isArray(data.docs)) {
      logger.warn(`${LOG_PREFIX} Invalid response structure`);
      return [];
    }

    logger.info(`${LOG_PREFIX} Received ${data.docs.length} FAB contacts`);

    // Filter by tenant AND isActive status
    const contacts = data.docs.filter((doc) => {
      if (!doc.isActive) return false;
      if (typeof doc.tenant === "object" && doc.tenant !== null && "slug" in doc.tenant) {
        return doc.tenant.slug === tenantSlug;
      }
      return false;
    });

    // Sort alphabetically by title
    contacts.sort((a, b) => a.title.localeCompare(b.title));

    logger.info(`${LOG_PREFIX} Found ${contacts.length} active contacts`);
    evictIfFull(fabContactsCache, MAX_CACHE_SIZE);
    fabContactsCache.set(tenantSlug, { data: contacts, expiresAt: Date.now() + CACHE_TTL_MS });
    return contacts;
  },
  "fabContactsByTenant"
);
